import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { X, Loader2, GitMerge, AlertTriangle, ArrowRight } from 'lucide-react';
import { useT } from '../utils/translate';
import { srv } from '../utils/serverText';
import { AssigneeAvatars } from './ui/AssigneePicker';
import { formatDate } from '../utils/date';
import { fmtCompact } from '../utils/format';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

const Check = ({ checked, onChange, children, disabled }) => (
  <label className={`flex items-start gap-2.5 text-sm ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
    <input type="checkbox" checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} className="mt-0.5 accent-primary-600 w-4 h-4 shrink-0" />
    <span className="text-ink">{children}</span>
  </label>
);

const statusCls = { active: 'bg-primary-50 text-primary-700', won: 'bg-emerald-50 text-emerald-700', lost: 'bg-red-50 text-red-600' };

// Окно объединения контактов: сначала показываем, что будет объединено (сделки и связанные данные),
// и только после выбора галочек выполняем объединение.
export default function ContactMergeDialog({ keepId, mergeIds, onClose, onDone }) {
  const t = useT();
  const [pv, setPv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dealIds, setDealIds] = useState(new Set());
  const [opts, setOpts] = useState({ tasks: true, calls: true, inbox: true, activities: true, files: true, fillEmpty: true });
  const [owners, setOwners] = useState('keep');

  useEffect(() => {
    let alive = true;
    axios.post(`${API}/contacts/merge/preview`, { keepId, mergeIds })
      .then(r => {
        if (!alive) return;
        setPv(r.data);
        setDealIds(new Set(r.data.merges.flatMap(m => m.deals.map(d => d._id))));
      })
      .catch(() => { toast.error(t('merge.loadError')); onClose(); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keepId]);

  const mergeDeals = useMemo(() => (pv ? pv.merges.flatMap(m => m.deals.map(d => ({ ...d, from: m.name }))) : []), [pv]);
  const sum = (k) => (pv ? pv.merges.reduce((s, m) => s + (m[k] || 0), 0) : 0);
  const allSelected = mergeDeals.length > 0 && mergeDeals.every(d => dealIds.has(d._id));
  const toggleDeal = (id) => setDealIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const setOpt = (k) => (v) => setOpts(o => ({ ...o, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/contacts/merge`, {
        keepId, deleteIds: mergeIds,
        options: { ...opts, moveDealIds: [...dealIds], assignees: owners },
      });
      toast.success(t('merge.success'));
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.message || t('merge.error'));
    } finally { setBusy(false); }
  };

  const fmtContact = (c) => (
    <div className="min-w-0">
      <p className="text-sm font-medium text-ink truncate">{srv(c.name)}</p>
      <p className="text-xs text-ink-tertiary truncate">
        {[c.phone, c.email].filter(Boolean).join(' · ') || '—'} · {formatDate(c.createdAt)}
      </p>
      <div className="flex flex-wrap gap-x-3 text-xs text-ink-tertiary mt-0.5">
        <span>{t('merge.deals')}: {c.deals.length}</span>
        <span>{t('merge.tasks')}: {c.tasks}</span>
        <span>{t('merge.calls')}: {c.calls}</span>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={busy ? undefined : onClose} />
      <div className="relative bg-white rounded-2xl shadow-modal w-full max-w-2xl flex flex-col max-h-[90dvh]">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-surface-100 shrink-0">
          <div>
            <h2 className="font-semibold text-ink flex items-center gap-2"><GitMerge className="w-4 h-4 text-primary-600" />{t('merge.title')}</h2>
            <p className="text-xs text-ink-tertiary mt-0.5">{t('merge.subtitle')}</p>
          </div>
          <button onClick={onClose} disabled={busy} className="p-1.5 rounded-lg hover:bg-surface-100 text-ink-tertiary"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-5">
          {loading || !pv ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-primary-400" /></div>
          ) : (
            <>
              {/* Контакты */}
              <section className="grid sm:grid-cols-[1fr_auto_1fr] gap-3 items-stretch">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3">
                  <p className="text-xs font-semibold text-emerald-700 mb-1.5">{t('merge.mainContact')}</p>
                  {fmtContact(pv.keep)}
                </div>
                <ArrowRight className="hidden sm:block w-4 h-4 text-ink-disabled self-center rotate-180" />
                <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3 space-y-2">
                  <p className="text-xs font-semibold text-amber-700">{t('merge.removedContacts')}</p>
                  {pv.merges.map(m => <div key={m._id}>{fmtContact(m)}</div>)}
                </div>
              </section>

              {/* Сделки */}
              <section>
                <div className="flex items-center justify-between mb-1.5">
                  <h3 className="text-sm font-semibold text-ink">{t('merge.deals')} <span className="text-ink-tertiary font-normal">({dealIds.size}/{mergeDeals.length})</span></h3>
                  {mergeDeals.length > 0 && (
                    <button type="button" className="text-xs text-primary-600 hover:underline"
                      onClick={() => setDealIds(allSelected ? new Set() : new Set(mergeDeals.map(d => d._id)))}>
                      {allSelected ? t('merge.clearAll') : t('merge.selectAll')}
                    </button>
                  )}
                </div>
                {mergeDeals.length === 0 ? (
                  <p className="text-sm text-ink-tertiary bg-surface-50 rounded-xl px-3 py-3">{t('merge.noDeals')}</p>
                ) : (
                  <>
                    <p className="text-xs text-ink-tertiary mb-2">{t('merge.dealsHint')}</p>
                    <div className="border border-surface-200 rounded-xl divide-y divide-surface-100 overflow-hidden">
                      {mergeDeals.map(d => (
                        <label key={d._id} className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer hover:bg-surface-50 ${dealIds.has(d._id) ? '' : 'bg-surface-50/60'}`}>
                          <input type="checkbox" checked={dealIds.has(d._id)} onChange={() => toggleDeal(d._id)} className="mt-1 accent-primary-600 w-4 h-4 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-ink truncate">{srv(d.title)}</span>
                              <span className={`text-xs font-medium px-1.5 py-px rounded-full ${statusCls[d.status] || statusCls.active}`}>{t(`merge.status.${d.status}`)}</span>
                              {pv.dupDealIds.includes(d._id) && (
                                <span className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-px rounded-full bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3" />{t('merge.possibleDup')}</span>
                              )}
                            </div>
                            <p className="text-xs text-ink-tertiary truncate">
                              {[d.funnelName, d.stageName].filter(Boolean).join(' › ')}{d.value > 0 ? ` · ${fmtCompact(d.value, t)}` : ''} · {t('merge.fromContact', { name: srv(d.from) })}
                            </p>
                          </div>
                          <AssigneeAvatars users={d.assignees} max={3} />
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </section>

              {/* Остальные данные */}
              <section>
                <h3 className="text-sm font-semibold text-ink mb-2">{t('merge.alsoMove')}</h3>
                <div className="grid sm:grid-cols-2 gap-2">
                  <Check checked={opts.tasks} onChange={setOpt('tasks')} disabled={sum('tasks') === 0}>{t('merge.tasks')}: {sum('tasks')}</Check>
                  <Check checked={opts.calls} onChange={setOpt('calls')} disabled={sum('calls') === 0}>{t('merge.calls')}: {sum('calls')}</Check>
                  <Check checked={opts.inbox} onChange={setOpt('inbox')} disabled={sum('inbox') === 0}>{t('merge.chats')}: {sum('inbox')}</Check>
                  <Check checked={opts.activities} onChange={setOpt('activities')} disabled={sum('activities') === 0}>{t('merge.history')}: {sum('activities')}</Check>
                  <Check checked={opts.files} onChange={setOpt('files')} disabled={sum('files') === 0}>{t('merge.files')}: {sum('files')}</Check>
                </div>
              </section>

              {/* Поля и ответственные */}
              <section className="space-y-3">
                <Check checked={opts.fillEmpty} onChange={setOpt('fillEmpty')}>{t('merge.fillEmpty')}</Check>
                {pv.conflicts.length > 0 && (
                  <div className="text-xs bg-surface-50 rounded-xl px-3 py-2.5 space-y-1">
                    <p className="font-medium text-ink-secondary">{t('merge.conflicts')}</p>
                    {pv.conflicts.map(c => (
                      <p key={c.field} className="text-ink-tertiary">
                        {t(`merge.field.${c.field}`)}: <span className="text-ink font-medium">{srv(c.keep) || '—'}</span>
                        <span className="mx-1">←</span>{c.others.map(srv).join(', ')}
                      </p>
                    ))}
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-ink mb-1.5">{t('merge.owners')}</p>
                  <div className="space-y-1.5">
                    <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" name="owners" checked={owners === 'keep'} onChange={() => setOwners('keep')} className="accent-primary-600" />{t('merge.ownersKeep')}</label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" name="owners" checked={owners === 'union'} onChange={() => setOwners('union')} className="accent-primary-600" />{t('merge.ownersUnion')}</label>
                  </div>
                </div>
              </section>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-surface-100 shrink-0">
          <p className="text-xs text-ink-tertiary hidden sm:block">{pv ? t('merge.summary', { n: dealIds.size }) : ''}</p>
          <div className="flex gap-2 ml-auto">
            <button onClick={onClose} disabled={busy} className="btn-secondary btn-md">{t('merge.cancel')}</button>
            <button onClick={submit} disabled={busy || loading} className="btn-primary btn-md flex items-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitMerge className="w-4 h-4" />}
              {t('merge.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
